import type { TableEditResult } from '../schemas'
import { useState, type Dispatch, type SetStateAction } from 'react'
import type { TableEdit, TableQuery } from '../schemas'

/** Selected rows are deleted atomically; failed requests can safely be retried. */
export function useTableDeletion({
  edit,
  discardDeleted,
  setQuery,
  reload,
}: {
  edit: (request: TableEdit) => Promise<TableEditResult>
  discardDeleted: (request: TableEdit) => void
  setQuery: Dispatch<SetStateAction<TableQuery>>
  reload: () => void
}) {
  const [deletion, setDeletion] = useState<{ title: string; request: TableEdit } | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const confirmDelete = async () => {
    if (!deletion || deleting) return
    setDeleting(true)
    setDeleteError(null)
    try {
      const result = await edit(deletion.request)
      if (!result.ok) return
      discardDeleted(deletion.request)
      // Let useTablePage reconcile removed columns so it can explain any lost filters or sorts.
      setQuery((current) => (current.offset === 0 ? current : { ...current, offset: 0 }))
      setDeletion(null)
    } catch (reason) {
      setDeleteError(reason instanceof Error ? reason.message : 'Unable to delete the table.')
      reload()
    } finally {
      setDeleting(false)
    }
  }

  return { deletion, setDeletion, deleting, deleteError, confirmDelete }
}
